import { SetupSteps } from "../../../../partials/content/SettingsNavigation";
import React, { useEffect, useRef, useState } from "react";

export function integrationUrl(host) {
  try {
    const url = new URL(host);
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    )
      return null;
    return url.origin + "/api/whatsapp/automation/connections";
  } catch {
    return null;
  }
}
export function buildN8nWorkflow(host, connection) {
  const url = integrationUrl(host);
  if (!url || !/^[a-z][a-z0-9-]{0,39}$/.test(connection.id))
    throw Error("INVALID_CONFIGURATION");
  return {
    name: "Xavier — " + connection.label,
    active: false,
    nodes: [
      {
        id: "manual",
        name: "Testar conexão",
        type: "n8n-nodes-base.manualTrigger",
        typeVersion: 1,
        position: [0, 0],
        parameters: {},
      },
      {
        id: "status",
        name: "Consultar WhatsApp",
        type: "n8n-nodes-base.httpRequest",
        typeVersion: 4.2,
        position: [260, 0],
        parameters: {
          url: url + "?connectionId=" + encodeURIComponent(connection.id),
          authentication: "genericCredentialType",
          genericAuthType: "httpBearerAuth",
          options: { timeout: 30000 },
        },
        notesInFlow: true,
        notes:
          "Em Credential for Bearer Auth, crie uma credencial. Cole a chave do portal no campo Bearer Token. Execute este fluxo e confira o resultado no portal.",
      },
      {
        id: "help",
        name: "Configuração",
        type: "n8n-nodes-base.stickyNote",
        typeVersion: 1,
        position: [0, -240],
        parameters: {
          width: 620,
          height: 180,
          content:
            "## Falta somente a credencial\n1. Abra Consultar WhatsApp → Credential for Bearer Auth → Create new.\n2. Cole a chave gerada no portal em **Bearer Token** e salve.\n3. Execute Testar conexão. No portal, clique em Verificar conexão.\nA URL já está configurada. Somente consulta; envios desligados.",
        },
      },
    ],
    connections: {
      "Testar conexão": {
        main: [[{ node: "Consultar WhatsApp", type: "main", index: 0 }]],
      },
    },
    settings: { executionOrder: "v1" },
  };
}
const blockerLabels = {
  N8N_PURPOSE_REQUIRED: "Marcar Automação n8n nas finalidades desta conta",
  SERVER_REQUIRED: "Configurar o servidor do WhatsApp",
  AUTOMATION_DISABLED: "Liberar consultas de automação neste ambiente",
  CREDENTIALS_DISABLED: "Preparar o cadastro protegido de credenciais",
};
export default function N8nIntegration({
  request,
  connections,
  identity,
  apiHost = process.env.REACT_APP_API_HOST,
}) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState("");
  const [state, setState] = useState({
    owner: "",
    id: "",
    busy: false,
    setup: null,
    token: null,
    error: "",
    notice: "",
  });
  const epoch = useRef(0);
  const selectedRow = connections.find((row) => row.id === selected);
  const url = integrationUrl(apiHost);
  const current =
    state.owner === identity && state.id === selected
      ? state
      : { busy: false, setup: null, token: null, error: "", notice: "" };
  useEffect(() => {
    ++epoch.current;
    setSelected("");
    setStep(0);
    setState({
      owner: identity,
      id: "",
      busy: false,
      setup: null,
      token: null,
      error: "",
      notice: "",
    });
  }, [identity]);
  useEffect(() => {
    if (selected && !selectedRow) {
      ++epoch.current;
      setSelected("");
      setStep(0);
      setState({
        owner: identity,
        id: "",
        busy: false,
        setup: null,
        token: null,
        error: "",
        notice: "",
      });
    }
  }, [identity, selected, selectedRow]);
  useEffect(() => {
    const hide = () => {
      if (document.hidden) {
        ++epoch.current;
        setState((value) => ({ ...value, busy: false, token: null }));
      }
    };
    document.addEventListener("visibilitychange", hide);
    return () => {
      ++epoch.current;
      document.removeEventListener("visibilitychange", hide);
    };
  }, []);
  useEffect(() => {
    if (!state.token) return undefined;
    const timer = setTimeout(
      () => setState((value) => ({ ...value, token: null })),
      60000
    );
    return () => clearTimeout(timer);
  }, [state.token]);
  async function operate(kind, id = selected) {
    if (!id || !connections.some((row) => row.id === id) || current.busy)
      return;
    const revision = ++epoch.current;
    setState({
      owner: identity,
      id,
      busy: true,
      setup: null,
      token: null,
      error: "",
      notice: "",
    });
    try {
      const result = await request({
        method: kind === "status" ? "GET" : "POST",
        url:
          "/whatsapp/integrations/" +
          id +
          (kind === "status" ? "" : "/" + kind),
      });
      if (epoch.current !== revision) return;
      if (kind === "credential") {
        if (
          !/^xwa_[a-f0-9]{64}$/.test(result.token) ||
          result.connectionId !== id ||
          result.authentication !== "BEARER" ||
          result.scope !== "connections:read" ||
          result.sendingEnabled !== false
        )
          throw Error();
        setState({
          owner: identity,
          id,
          busy: false,
          setup: {
            enabled: true,
            blockers: [],
            credential: { active: true, lastSeenAt: null },
          },
          token: result.token,
          error: "",
          notice:
            "Chave pronta para copiar por 60 segundos. Validade: 30 dias.",
        });
      } else if (kind === "revoke") {
        if (result.revoked !== true || result.sendingEnabled !== false)
          throw Error();
        setState({
          owner: identity,
          id,
          busy: false,
          setup: { enabled: true, blockers: [], credential: null },
          token: null,
          error: "",
          notice: "Acesso revogado. Gere outra chave para reconectar.",
        });
      } else {
        if (
          typeof result.enabled !== "boolean" ||
          result.sendingEnabled !== false ||
          !Array.isArray(result.blockers) ||
          result.blockers.some((key) => !blockerLabels[key]) ||
          (result.credential &&
            (typeof result.credential.active !== "boolean" ||
              (result.credential.lastSeenAt &&
                !Number.isFinite(Date.parse(result.credential.lastSeenAt)))))
        )
          throw Error();
        setState({
          owner: identity,
          id,
          busy: false,
          setup: result,
          token: null,
          error: "",
          notice: "",
        });
      }
    } catch {
      if (epoch.current === revision)
        setState({
          owner: identity,
          id,
          busy: false,
          setup: null,
          token: null,
          error:
            "Não foi possível concluir. Verifique a configuração e tente novamente.",
          notice: "",
        });
    }
  }
  async function copy(value) {
    const revision = epoch.current;
    try {
      await navigator.clipboard.writeText(value);
      if (revision === epoch.current)
        setState((old) => ({
          ...old,
          notice: "Copiado.",
        }));
    } catch {
      if (revision === epoch.current)
        setState((old) => ({
          ...old,
          error:
            "O navegador não permitiu copiar. Tente novamente em uma janela segura do portal.",
        }));
    }
  }
  function download() {
    const blob = new Blob(
      [JSON.stringify(buildN8nWorkflow(apiHost, selectedRow), null, 2)],
      { type: "application/json" }
    );
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = "xavier-n8n-" + selected + ".json";
    link.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  }
  const confirmed =
    current.setup?.credential?.active && current.setup.credential.lastSeenAt;
  const canAdvance = Boolean(selectedRow && url && !current.busy);
  function go(next) {
    if (current.busy) return;
    if (next === 0 || canAdvance) setStep(next);
  }
  return (
    <section aria-label="Assistente de integração n8n">
      <SetupSteps
        steps={["Conexão", "Configurar", "Conferir"]}
        current={step}
        onChange={go}
      />
      <div className="xv-setup-content">
        {step === 0 && (
          <>
            <h2>Integrar com n8n</h2>
            <p className="xv-settings-caption">
              Escolha a conta que o fluxo vai consultar.
            </p>
            <label className="sr-only" htmlFor="n8n-connection">
              Conexão do WhatsApp
            </label>
            <select
              className="form-control"
              id="n8n-connection"
              value={selectedRow ? selected : ""}
              disabled={current.busy}
              onChange={(event) => {
                const id = event.target.value;
                ++epoch.current;
                setSelected(id);
                setState({
                  owner: identity,
                  id,
                  busy: false,
                  setup: null,
                  token: null,
                  error: "",
                  notice: "",
                });
                if (id) operate("status", id);
              }}
            >
              <option value="">Selecione uma conexão</option>
              {connections.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.label} — {row.expectedNumberMasked}
                </option>
              ))}
            </select>
            {!connections.length && (
              <p className="xv-settings-note">
                Cadastre uma instância na aba Conexões.
              </p>
            )}
          </>
        )}
        {step === 1 && (
          <>
            <h2>Configure o n8n</h2>
            <p className="xv-settings-caption">
              Importe o fluxo e cole a chave em <strong>Bearer Token</strong>.
            </p>
            <div className="mt-4 mb-4">
              <p className="mb-2">
                <strong>Fluxo pronto</strong>
              </p>
              <div className="xv-settings-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!url}
                  onClick={download}
                >
                  Baixar fluxo configurado
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!url}
                  onClick={() =>
                    copy(
                      JSON.stringify(
                        buildN8nWorkflow(apiHost, selectedRow),
                        null,
                        2
                      )
                    )
                  }
                >
                  Copiar fluxo para n8n
                </button>
              </div>
              <p className="xv-settings-caption mt-2">
                URL e conta já preenchidas.
              </p>
            </div>
            <div className="mt-4">
              <p className="mb-2">
                <strong>Chave de acesso</strong>
              </p>
              <div className="xv-settings-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={current.busy || !current.setup?.enabled || !url}
                  onClick={() => operate("credential")}
                >
                  {current.setup?.credential?.active
                    ? "Renovar chave de integração"
                    : "Gerar chave de integração"}
                </button>
                {current.token && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => copy(current.token)}
                  >
                    Copiar chave para Bearer Token
                  </button>
                )}
              </div>
            </div>
            <details className="mt-3">
              <summary className="xv-settings-caption">
                Onde colar a chave?
              </summary>
              <p className="xv-settings-caption mt-2">
                No n8n: Consultar WhatsApp → Credential for Bearer Auth → Create
                new → Bearer Token.
              </p>
            </details>
            {current.setup?.credential?.active && (
              <details className="mt-3">
                <summary className="xv-settings-caption">
                  Gerenciar acesso
                </summary>
                <p className="xv-settings-caption mt-2 mb-2">
                  Renovar invalida a chave anterior.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={current.busy}
                  onClick={() => operate("revoke")}
                >
                  Revogar acesso
                </button>
              </details>
            )}
          </>
        )}
        {step === 2 && (
          <>
            <h2>
              {confirmed ? "Integração confirmada" : "Confira a integração"}
            </h2>
            <p className="xv-settings-caption">
              {confirmed
                ? "A chave atual já foi usada para consultar o portal."
                : "Execute o fluxo no n8n e verifique o acesso aqui."}
            </p>
            <div className="xv-settings-actions mt-4">
              <button
                type="button"
                className="btn btn-primary"
                disabled={current.busy || !current.setup?.enabled}
                onClick={() => operate("status")}
              >
                Verificar conexão com n8n
              </button>
            </div>
            {current.setup?.enabled && !confirmed && (
              <p className="xv-settings-caption mt-3">
                Ainda não recebemos um acesso com a credencial atual.
              </p>
            )}
          </>
        )}
        {current.busy && (
          <p role="status" className="xv-settings-caption mt-3">
            Conferindo integração…
          </p>
        )}
        {current.error && (
          <p role="alert" className="xv-settings-note">
            {current.error}
          </p>
        )}
        {current.notice && (
          <p role="status" className="xv-settings-note">
            {current.notice}
          </p>
        )}
        {selectedRow && !url && (
          <p role="alert" className="xv-settings-note">
            A URL da API deste ambiente precisa ser configurada.
          </p>
        )}
        {current.setup?.blockers.length > 0 && (
          <details className="xv-settings-note">
            <summary>Integração aguardando liberação</summary>
            <ul className="mt-2 mb-0">
              {current.setup.blockers.map((key) => (
                <li key={key}>{blockerLabels[key]}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <div className="xv-setup-footer">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={step === 0 || current.busy}
          onClick={() => go(step - 1)}
        >
          Voltar
        </button>
        {step < 2 ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canAdvance}
            onClick={() => go(step + 1)}
          >
            Continuar
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={current.busy}
            onClick={() => go(0)}
          >
            Escolher outra conexão
          </button>
        )}
      </div>
    </section>
  );
}
